import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Plus, MapPin, Loader2, Hammer } from "lucide-react";
import { NewUpdateModal } from "@/pages/admin/AdminLeads";

// Obra em curso = execução efetivamente iniciada via INICIAR EXECUÇÃO (e ainda não concluída).
export const isObraEmCurso = (l) => l.status === "Execução" && !!l.execution_started_at;

const fetchLastUpdate = async (leadId) => {
    try {
        const { data } = await api.get(`/admin/leads/${leadId}/updates`);
        return (data || [])[0] || null;
    } catch { return null; }
};

const fmt = (ymd) => {
    const [y, m, d] = String(ymd || "").split("-").map((n) => parseInt(n, 10));
    return y && m && d ? new Date(y, m - 1, d).toLocaleDateString("pt-PT") : "—";
};

export default function AdminObrasEmCurso() {
    const [obras, setObras] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/leads", { params: { status: "Execução" } });
            const list = data.filter(isObraEmCurso);
            const lasts = await Promise.all(list.map((l) => fetchLastUpdate(l.id)));
            setObras(list.map((lead, i) => ({ lead, last: lasts[i] })));
        } catch { setObras([]); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const onPublished = async () => {
        const id = updatingId;
        setUpdatingId(null);
        const last = await fetchLastUpdate(id);
        setObras((prev) => prev.map((o) => (o.lead.id === id ? { ...o, last } : o)));
    };

    return (
        <div data-testid="obras-em-curso-page">
            <h1 className="font-display text-3xl font-bold text-stone-900 mb-1" data-testid="obras-em-curso-title">Obras em curso</h1>
            <p className="text-stone-500 text-sm mb-6">Clientes com execução iniciada. Publique uma atualização sem abrir a ficha.</p>
            {obras === null ? (
                <div className="flex justify-center py-16 text-stone-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : obras.length === 0 ? (
                <div className="bg-white rounded-xl border border-stone-200 p-10 text-center text-stone-500 text-sm" data-testid="obras-em-curso-empty">
                    <Hammer className="w-6 h-6 mx-auto mb-3 text-stone-300" />
                    Sem obras em curso. Uma obra aparece aqui depois de carregar em <strong>Iniciar execução</strong> na ficha do cliente.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="obras-em-curso-list">
                    {obras.map((o) => (
                        <ObraCard key={o.lead.id} lead={o.lead} last={o.last} onUpdate={() => setUpdatingId(o.lead.id)} />
                    ))}
                </div>
            )}
            {updatingId && (
                <NewUpdateModal leadId={updatingId} onClose={() => setUpdatingId(null)} onPublished={onPublished} />
            )}
        </div>
    );
}

function ObraCard({ lead, last, onUpdate }) {
    const ident = [lead.location, (lead.work_types || []).slice(0, 2).join(", ")].filter(Boolean).join(" · ");
    return (
        <div className="bg-white rounded-xl border border-stone-200 p-4 flex flex-col" data-testid={`obra-card-${lead.id}`}>
            <div className="font-medium text-stone-900 truncate" data-testid={`obra-name-${lead.id}`}>{lead.name}</div>
            {ident && (
                <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1.5 min-w-0">
                    <MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{ident}</span>
                </div>
            )}
            <div className="mt-3 space-y-1.5 text-sm flex-1">
                <div>
                    <span className="text-stone-500">Última atualização: </span>
                    <span className="text-stone-900 font-medium" data-testid={`obra-last-update-${lead.id}`}>
                        {last ? fmt(last.update_date) : "ainda sem atualizações"}
                    </span>
                </div>
                <div>
                    <span className="text-stone-500">A seguir: </span>
                    <span className="text-stone-900" data-testid={`obra-next-step-${lead.id}`}>{last?.next_step || "—"}</span>
                </div>
            </div>
            <div className="mt-4 flex gap-2">
                <button
                    type="button"
                    onClick={onUpdate}
                    className="flex-1 min-h-[52px] flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--copper))] text-white text-sm font-semibold uppercase tracking-wide hover:opacity-90 active:opacity-80 transition-opacity"
                    data-testid={`obra-add-update-${lead.id}`}
                >
                    <Plus className="w-5 h-5" /> Atualização
                </button>
                <Link
                    to={`/admin/clientes?lead=${lead.id}`}
                    className="min-h-[52px] px-6 flex items-center justify-center rounded-full border border-stone-300 text-stone-700 text-sm font-semibold uppercase tracking-wide hover:border-stone-400 transition-colors"
                    data-testid={`obra-open-${lead.id}`}
                >
                    Abrir
                </Link>
            </div>
        </div>
    );
}
